import { inject, Injectable } from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';

import moment from 'moment';
import { get, set } from 'lodash';
import { ColDef } from 'ag-grid-community';
import formatMoney from 'accounting-js/lib/formatMoney.js';
import formatNumber from 'accounting-js/lib/formatNumber.js';
import { TsTableUtils } from '../ts-table/ts-table.utils';
import { ColumnDef, OptionsFilterFn } from '../ts-table/ts-table.model';
import { TsAgMultiSelectEditor } from '../ts-table/renderer-components/table-select-editor.component';
import { TsAgMultiSelectRenderer } from '../ts-table/renderer-components/table-select-renderer.component';
import { TableTagsRendererComponent } from '../ts-table/renderer-components/table-tags-renderer.component';
import { TableActionsRendererComponent } from '../ts-table/renderer-components/table-actions-renderer.component';
import { TableAggregationRendererComponent } from '../ts-table/renderer-components/table-aggregation-renderer.component';
import { TsAgCoordinatesEditor } from '../ts-table/renderer-components/table-coordinates-editor/table-coordinates-editor.component';

@Injectable({
  providedIn: 'root',
})
export class TsTableService {
  convertFieldDefsToColDefs(
    fieldDefs: ColumnDef[],
    relatedToRedirectConfig: any,
    data?: any,
    readOnly: boolean = false,
    optionsFilterFn: OptionsFilterFn = null,
    clientSorting = false,
    cellDataType: 'inherit' | false = 'inherit',
  ): ColDef[] {
    const fields = fieldDefs.map((fieldDef) => {
      const colDef: ColDef = {
        ...this.setFieldSettings(
          fieldDef,
          relatedToRedirectConfig,
          data,
          readOnly,
          optionsFilterFn,
          clientSorting,
          cellDataType,
        ),
      };
      return colDef;
    });

    return fields;
  }

  setFieldSettings(
    fieldDef: ColumnDef,
    relatedToRedirectConfig: any,
    data?: any,
    readOnly?: boolean,
    optionsFilterFn: OptionsFilterFn = null,
    clientSorting = false,
    cellDataType: 'inherit' | false = 'inherit',
  ): ColDef {
    if (!fieldDef) {
      return;
    }
    const colDef: ColDef = {
      colId: fieldDef.tsTableOptions?.colId ?? undefined,
      headerName: this.isNestedField(fieldDef)
        ? (fieldDef._parentFieldLabel ? fieldDef._parentFieldLabel + ' ' : '') + fieldDef.label
        : fieldDef.label || fieldDef.tsTableOptions?.headerName,
      headerClass: fieldDef.tsTableOptions?.headerClass,
      headerTooltip: fieldDef.tsTableOptions?.headerTooltip,
      headerComponentParams: fieldDef.tsTableOptions?.headerComponentParams,
      autoHeaderHeight: true,
      suppressHeaderMenuButton: fieldDef.tsTableOptions?.suppressHeaderMenuButton ?? true,
      suppressHeaderContextMenu: fieldDef.tsTableOptions?.suppressHeaderContextMenu ?? true,
      headerCheckboxSelection: fieldDef.tsTableOptions?.headerCheckboxSelection,
      field: fieldDef.tsTableOptions?.field || fieldDef.name,
      sortable: this.isNestedField(fieldDef)
        ? false
        : fieldDef.tsTableOptions?.sortable !== undefined
          ? fieldDef.tsTableOptions.sortable
          : !!fieldDef.meta?.sorting,
      filter: false,
      cellDataType: cellDataType === 'inherit' ? (typeof fieldDef.type === 'function' ? false : fieldDef.type) : false,
      editable: (params) => {
        if (
          readOnly ||
          this.isNestedField(fieldDef) ||
          params.data.isCategory ||
          fieldDef.meta?.readonly ||
          fieldDef.locked ||
          fieldDef.meta?.value_hidden ||
          params.data.permissions?.is_editable === false
        ) {
          return false;
        }
        if (typeof fieldDef.tsTableOptions?.editable === 'function') {
          return fieldDef.tsTableOptions.editable(params);
        }
        return fieldDef.tsTableOptions?.editable ?? true;
      },
      resizable: fieldDef.tsTableOptions?.resizable,
      checkboxSelection: fieldDef.tsTableOptions?.checkboxSelection,
      suppressMovable: fieldDef.tsTableOptions?.suppressMovable,
      lockPosition: fieldDef.tsTableOptions?.lockPosition,
      lockPinned: fieldDef.tsTableOptions?.lockPinned,
      width: fieldDef.tsTableOptions?.width,
      minWidth: fieldDef.tsTableOptions?.minWidth,
      maxWidth: fieldDef.tsTableOptions?.maxWidth,
      cellEditorPopup: fieldDef.tsTableOptions?.cellEditorPopup,
      flex: fieldDef.tsTableOptions?.flex,
      suppressAutoSize: fieldDef.tsTableOptions?.suppressAutoSize,
      suppressSizeToFit: fieldDef.tsTableOptions?.suppressSizeToFit,
      enableValue: fieldDef.tsTableOptions?.enableValue,
      cellStyle: fieldDef.tsTableOptions?.cellStyle,
      autoHeight: fieldDef.tsTableOptions?.autoHeight,
      pinned: fieldDef.tsTableOptions?.pinned,
      rowGroup: fieldDef.tsTableOptions?.rowGroup,
      hide: fieldDef.tsTableOptions?.rowGroup,
      aggFunc: fieldDef.tsTableOptions?.aggFunc,
      cellClass: fieldDef.tsTableOptions?.cellClass,
      cellClassRules: fieldDef.tsTableOptions?.cellClassRules,
      context: fieldDef,
      sort: fieldDef.tsTableOptions?.sort,
      wrapText: fieldDef.tsTableOptions?.wrapText,
      onCellClicked: fieldDef.tsTableOptions?.onCellClicked,
      onCellDoubleClicked: fieldDef.tsTableOptions?.onCellDoubleClicked,
      tooltipField: fieldDef.tsTableOptions?.tooltipField,
      contextMenuItems: fieldDef.tsTableOptions?.contextMenuItems,
      tooltipValueGetter: fieldDef.tsTableOptions?.tooltipValueGetter,
      cellRendererSelector: fieldDef.tsTableOptions?.cellRendererSelector,
      cellEditorSelector: fieldDef.tsTableOptions?.cellEditorSelector,
      valueFormatter: fieldDef.tsTableOptions?.valueFormatter || this.setValueFormatter(fieldDef, optionsFilterFn),
      valueGetter: fieldDef.tsTableOptions?.valueGetter,
      ...this.setCellEditor(fieldDef, relatedToRedirectConfig, data, optionsFilterFn),
      ...this.setCellRenderer(fieldDef, relatedToRedirectConfig),
    };

    if (!clientSorting) {
      colDef.comparator = () => 0;
    }

    return colDef;
  }

  private isNestedField(fieldDef: ColumnDef): boolean {
    return fieldDef.name.includes('.');
  }

  setValueFormatter(fieldDef: ColumnDef, optionsFilterFn: OptionsFilterFn = null): (params: any) => string {
    switch (fieldDef?.type) {
      case 'date':
      case 'datetime':
        return (params) => {
          const value: any = this.getValueFromParam(fieldDef, params);

          return value ? moment(value).format('MM/DD/YYYY') : '';
        };
      case 'number':
        return (params) => {
          let value: any = this.getValueFromParam(fieldDef, params);

          // If scale is defined, format with precision like decimal type
          if (fieldDef.meta?.scale !== null && fieldDef.meta?.scale !== undefined) {
            value = value ? parseFloat(value) : null;
            return value !== null ? formatNumber(value, { precision: fieldDef.meta.scale }) : '';
          }

          return TsTableUtils.formatNumberWithFieldRules(value, fieldDef);
        };
      case 'decimal':
        return (params) => {
          let value: any = this.getValueFromParam(fieldDef, params);

          if (!fieldDef.meta?.scale) {
            return value;
          }

          value = value ? parseFloat(value) : null;

          const formattedValue = value !== null ? formatNumber(value, { precision: fieldDef.meta?.scale || 0 }) : '';

          return formattedValue;
        };
      case 'money':
        return (params) => {
          let value: any = this.getValueFromParam(fieldDef, params);

          value = value !== null && value !== undefined ? parseFloat(value) : null;

          const formattedValue = value !== null ? formatMoney(value, { precision: fieldDef.meta?.scale || 0 }) : '';
          return formattedValue;
        };
      case 'percent':
        return (params) => {
          const value: any = this.getValueFromParam(fieldDef, params);

          const formattedValue = value ? `${formatNumber(value, { precision: fieldDef.meta?.scale || 0 })}%` : '';

          return formattedValue;
        };
      case 'select':
        return (params) => {
          if (fieldDef.name.includes('.')) {
            const nestedValues = this.getLinkedFieldValue(params.data, fieldDef.name);
            if (nestedValues) {
              return nestedValues
                .map((nestedValue) => {
                  const labelOption = this.findOptionById(nestedValue.label, params.colDef.cellEditorParams?.options);
                  return labelOption ? labelOption.name || labelOption.full_name : nestedValue.label;
                })
                .join(', ');
            }
          } else {
            const selectedOption = params.colDef.cellEditorParams?.options?.find((option) => option.id == params.value);

            // if (!selectedOption && optionsFilterFn?.[params.colDef.field]) {
            //   return '';
            // }

            return selectedOption ? selectedOption.name : params.value;
          }
        };
      case 'text':
        return (params) => {
          const value: any = this.getValueFromParam(fieldDef, params);
          return value;
        };
      default: {
        return (params) => null;
      }
    }
  }

  findOptionById(id: string | number, options: any[]): any {
    return options?.find((option) => option.id == id);
  }

  getLinkedFieldValue(object: any, fieldName: string): { label: string; [key: string]: any }[] {
    const firstLevel = fieldName.split('.')[0];
    const propLevel = fieldName.split('.')[1];
    if (Array.isArray(object[firstLevel])) {
      return object[firstLevel].map((item) => ({ label: item[propLevel], ...item }));
    }

    const label = get(object, fieldName);
    if (label) {
      return [{ label: get(object, fieldName), ...object[firstLevel] }];
    }
  }

  setCellEditor(
    fieldDef: ColumnDef,
    relatedToRedirectConfig: any,
    data?: any,
    optionsFilterFn: OptionsFilterFn = null,
  ): Partial<ColDef> {
    if (fieldDef.meta?.value_hidden) {
      return {
        editable: false,
      };
    }

    if (fieldDef.tsTableOptions?.cellEditor) {
      return {
        cellEditor: fieldDef.tsTableOptions.cellEditor,
        cellEditorParams: fieldDef.tsTableOptions.cellEditorParams || {},
      };
    }

    if (fieldDef.locked) {
      return {
        editable: false,
      };
    }

    switch (fieldDef.type) {
      case 'number':
      case 'decimal':
        const cellEditor = (fieldDef.name === 'latitude' || fieldDef.name === 'longitude') ? TsAgCoordinatesEditor : 'agNumberCellEditor';

        return {
          cellEditor: cellEditor,
          ...(cellEditor === TsAgCoordinatesEditor ? { cellEditorPopup: true } : {}),
          cellEditorParams: {
            precision: fieldDef.meta?.scale,
            showStepperButtons: false,
          },
        };
      case 'money':
        return {
          cellEditor: 'agNumberCellEditor',
          cellEditorParams: {
            precision: fieldDef.meta?.scale || 0,
            showStepperButtons: false,
          },
        };
      case 'year':
        return {
          cellEditor: 'agNumberCellEditor',
          cellEditorParams: {
            precision: 0,
            showStepperButtons: false,
            min: 1900,
            max: 9999,
          },
        };
      case 'date':
      case 'datetime':
        return {
          cellEditor: 'agDateStringCellEditor',
          cellDataType: 'dateString',
        };
      case 'checkbox':
        return {
          cellEditor: 'agCheckboxCellEditor',
        };
      case 'textarea':
        return {
          cellEditor: 'agLargeTextCellEditor',
          cellEditorParams: {
            maxLength: 10000,
          },
        };
      case 'text':
      case 'url':
        return {
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 1024,
          },
        };
      case 'select':
        let options = fieldDef.meta?.options;
        if (options?.length > 0) {
          if (options.every((option) => typeof option === 'string'))
            options = fieldDef.meta.options.map((value) => ({ id: value, name: value }));
        } else {
          if (fieldDef.related_to_type === 'Task' && fieldDef.name === 'assigned_user_id') {
            options = data?.users
              ?.filter((option) => option && !option?.hidden)
              .map((option) => ({ id: option.id, name: option.name }));
          } else {
            options = data?.options[fieldDef.meta.option_name]?.filter((option) => option && !option?.hidden);
          }
        }

        if (optionsFilterFn?.[fieldDef.name]) {
          return {
            valueFormatter: (vf) => {
              if (vf.data.isCategory === true) {
                return '';
              }

              return options?.find((option) => option.id == vf.value)?.name ?? '';
            },
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: (cep) => {
              return {
                formatValue: (fv) => {
                  return typeof fv === 'object' ? fv.name : (options?.find((option) => option.id == fv)?.name ?? '');
                },
                values: optionsFilterFn[fieldDef.name](options, cep.data).map((option) => option.id),
                allowTyping: true,
                filterList: true,
                multiSelect: fieldDef.meta?.multi_select,
                highlightMatch: true,
                valueListMaxHeight: 220,
              };
            },
          };
        }

        return {
          cellEditor: 'agRichSelectCellEditor',
          cellEditorParams: {
            values: options?.map((option) => option.id) || [],
            options: options,
            searchType: 'match',
            allowTyping: true,
            filterList: true,
            multiSelect: fieldDef.meta?.multi_select,
            highlightMatch: true,
            valueListMaxHeight: 220,
          },
        };
      case 'record': {
        const custom = this.getRecordTypeCellEditor(fieldDef, relatedToRedirectConfig, data, optionsFilterFn);
        if (custom) {
          return custom;
        }
        return {
          cellEditor: TsAgMultiSelectEditor,
          cellRenderer: TsAgMultiSelectRenderer,
          cellEditorParams: {
            multiSelect: fieldDef.meta?.multi_select,
            recordType: fieldDef.meta?.record_type,
            options: fieldDef.meta?.options,
          },
          cellRendererParams: {
            relatedToRedirectConfig,
            recordType: fieldDef.meta?.record_type,
          },
        };
      }
      default:
        return {
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 1024,
            ...fieldDef.tsTableOptions?.cellEditorParams,
          },
        };
    }
  }

  setCellRenderer(fieldDef: ColumnDef, relatedToRedirectConfig: any): ColDef {
    if (fieldDef.meta?.value_hidden) {
      return {
        cellRenderer: (params) => {
          if (!params.data.isCategory) {
            return '<span style="filter: blur(2px); font-style: italic; letter-spacing: 2px;">REDACTED</span>';
          }
        },
      };
    }
    if (fieldDef.tsTableOptions?.cellRenderer) {
      return {
        cellRenderer: fieldDef.tsTableOptions.cellRenderer,
        cellRendererParams: { ...fieldDef.tsTableOptions.cellRendererParams, relatedToRedirectConfig },
      };
    }

    // Allow subclasses to override renderer for record types
    if (fieldDef.type === 'record') {
      const custom = this.getRecordTypeCellRenderer(fieldDef, relatedToRedirectConfig);
      if (custom) {
        return custom;
      }
    }

    switch (fieldDef.type) {
      case 'tags':
        return {
          cellRenderer: TableTagsRendererComponent,
          cellEditor: fieldDef.tsTableOptions?.cellEditor,
        };
      case 'checkbox':
        return {
          cellRenderer: 'agCheckboxCellRenderer',
          valueGetter: (params) => {
            if (params.data?.isCategory) return;
            const val = get(params.data, params.colDef.field);
            return val === true || val === 'true';
          },
          valueSetter: (params) => {
            if (params.data?.isCategory) return false;
            set(params.data, params.colDef.field, params.newValue);
            return true;
          },
        };
      case 'url':
        return {
          cellRenderer: (params) => {
            const url = params.value;
            return url ? `<a href="${url}" target="_blank" style="text-decoration: underline;">${url}</a>` : '';
          },
        };
      case 'money':
      case 'number':
      case 'decimal':
      case 'percent':
        return {
          cellRendererSelector: (params) => {
            const isCategory = params.data?.isCategory || false;

            if (isCategory) {
              const summaryColumns = params.data?.summaryColumns;
              if (!summaryColumns || typeof summaryColumns !== 'object') {
                return;
              }
              const transformedSummaryColumns = Object.keys(summaryColumns || {}).reduce((acc, key) => {
                const newKey = key.replace(/^group_/, '');
                acc[newKey] = summaryColumns[key];
                return acc;
              }, {});

              const fieldName = params.colDef.field;

              if (fieldName in transformedSummaryColumns) {
                const value = transformedSummaryColumns[fieldName];
                return {
                  component: TableAggregationRendererComponent,
                  params: { value },
                };
              }
            }
          },
          cellRendererParams: { relatedToRedirectConfig },
        };
      default:
        return;
    }
  }

  setActionsColumn(params): ColDef {
    return {
      headerName: 'Actions',
      field: 'actions',
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      resizable: false,
      suppressMovable: true,
      cellRenderer: TableActionsRendererComponent,
      cellRendererParams: params,
    };
  }

  public static moveColumns(
    event: any,
    displayColumns: string[],
    excludedFields: string[], // cols that we ignore (like 'openDetails', 'checkbox')
    updateColumnsCallback: (columns: string[]) => void, // cb function to update the columns
  ): void {
    if (event.toIndex !== undefined && event.finished) {
      const movedColumn = event.column?.colDef?.context?.name;

      if (movedColumn && !excludedFields.includes(movedColumn)) {
        const previousIndex = displayColumns.indexOf(movedColumn);
        const currentIndex = event.toIndex - excludedFields.length; // index adjustment based on the columns that we're going to ignore

        if (previousIndex !== currentIndex && previousIndex > -1) {
          moveItemInArray(displayColumns, previousIndex, currentIndex);
          updateColumnsCallback(displayColumns);
        }
      }
    }
  }

  getValueFromParam(columnDef: ColumnDef, params: any): any {
    let value: any;

    if (columnDef.name.indexOf('.') > -1) {
      const fieldName = columnDef.name.split('.').pop();
      value = params.value?.[fieldName];
    } else {
      value = params.value;
    }

    return value;
  }

  setCellRendererSelector(fieldDef, relatedToRedirectConfig?: any, data?: any): any {
    switch (fieldDef?.type) {
      case 'tags':
        return { component: TableTagsRendererComponent };

      case 'checkbox':
        return { component: 'agCheckboxCellRenderer' };

      case 'url':
        return {
          component: 'agTextCellRenderer',
          params: {
            renderer: (params) => {
              const url = params.value;
              return url ? `<a href="${url}" target="_blank" style="text-decoration: underline;">${url}</a>` : '';
            },
          },
        };

      case 'number':
        return {
          component: 'agTextCellRenderer',
          params: {
            renderer: (params) => {
              let value: any = this.getValueFromParam(fieldDef, params);
              if (!fieldDef.meta?.scale) return value;
              value = value ? parseFloat(value) : null;
              return value !== null ? formatNumber(value, { precision: fieldDef.meta?.scale || 0 }) : '';
            },
          },
        };

      case 'money':
        return {
          component: 'agTextCellRenderer',
          params: {
            renderer: (params) => {
              let value: any = this.getValueFromParam(fieldDef, params);
              value = value !== null && value !== undefined ? parseFloat(value) : null;
              return value !== null ? formatMoney(value) : '';
            },
          },
        };

      case 'percent':
        return {
          component: 'agTextCellRenderer',
          params: {
            renderer: (params) => {
              const value: any = this.getValueFromParam(fieldDef, params);
              return value ? `${value}%` : '';
            },
          },
        };

      case 'select':
        return {
          component: 'agTextCellRenderer',
          params: {
            renderer: (params) => {
              let options = fieldDef.meta?.options;
              if (options?.length > 0) {
                options = fieldDef.meta.options.map((value) => ({ id: value, name: value }));
              } else {
                options = data?.options[fieldDef.meta.option_name]
                  ?.filter((option) => option && !option?.hidden)
                  .map((option) => ({ id: option.id, name: option.name }));
              }
              if (fieldDef.name.includes('.')) {
                const nestedValues = this.getLinkedFieldValue(params.data, fieldDef.name);
                if (nestedValues) {
                  return nestedValues
                    .map((nestedValue) => {
                      const labelOption = this.findOptionById(nestedValue.label, options);
                      return labelOption ? labelOption.name || labelOption.full_name : nestedValue.label;
                    })
                    .join(', ');
                }
              } else {
                const selectedOption = options?.find((option) => option.id == params.value);
                return selectedOption ? selectedOption.name : params.value;
              }
            },
          },
        };

      case 'record': {
        const custom = this.getRecordTypeCellRendererSelector(fieldDef, relatedToRedirectConfig, data);
        if (custom) {
          return custom;
        }
        return {
          component: TsAgMultiSelectRenderer,
          params: {
            recordType: fieldDef.meta?.record_type,
            relatedToRedirectConfig: relatedToRedirectConfig,
          },
        };
      }

      case 'text':
        return {
          component: 'agTextCellRenderer',
        };

      default:
        return null;
    }
  }

  setCellEditorSelector(fieldDef, relatedToRedirectConfig: any, data?: any): any {
    if (fieldDef.meta?.value_hidden) {
      return {
        component: null,
      };
    }

    if (fieldDef.locked) {
      return {
        component: null,
      };
    }

    switch (fieldDef?.type) {
      case 'number':
        return {
          component: 'agNumberCellEditor',
          params: {
            precision: fieldDef.meta?.scale || 0,
            showStepperButtons: false,
          },
        };
      case 'decimal':
        const cellEditor = (fieldDef.name === 'latitude' || fieldDef.name === 'longitude') ? TsAgCoordinatesEditor : 'agNumberCellEditor';
        return {
          component: cellEditor,
          ...(cellEditor === TsAgCoordinatesEditor ? { cellEditorPopup: true } : {}),
          params: {
            precision: fieldDef.meta?.scale,
            showStepperButtons: false,
          },
        };
      case 'money':
        return {
          component: 'agNumberCellEditor',
          params: {
            precision: fieldDef.meta?.scale || 0,
            showStepperButtons: false,
          },
        };
      case 'year':
        return {
          component: 'agNumberCellEditor',
          params: {
            precision: 0,
            showStepperButtons: false,
            min: 1900,
            max: 9999,
          },
        };
      case 'date':
      case 'datetime':
        return {
          component: 'agDateStringCellEditor',
          params: {
            cellDataType: 'dateString',
          },
        };
      case 'checkbox':
        return {
          component: 'agCheckboxCellEditor',
        };
      case 'textarea':
        return {
          component: 'agLargeTextCellEditor',
          cellEditorParams: {
            maxLength: 10000,
          },
        };
      case 'text':
      case 'url':
        return {
          component: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 1024,
          },
        };
      case 'select':
        let options = fieldDef.meta?.options;
        if (options?.length > 0) {
          options = fieldDef.meta.options.map((value) => ({ id: value, name: value }));
        } else {
          options = data?.options[fieldDef.meta.option_name]
            ?.filter((option) => option && !option?.hidden)
            .map((option) => ({ id: option.id, name: option.name }));
        }

        return {
          component: 'agRichSelectCellEditor',
          params: {
            values: options?.map((option) => option.id) || [],
            options: options,
            allowTyping: true,
            filterList: true,
            multiSelect: fieldDef.meta?.multi_select,
            highlightMatch: true,
            valueListMaxHeight: 220,
          },
        };
      case 'record': {
        const custom = this.getRecordTypeCellEditorSelector(fieldDef, relatedToRedirectConfig, data);
        if (custom) {
          return custom;
        }
        return {
          component: TsAgMultiSelectEditor,
          params: {
            multiSelect: fieldDef.meta?.multi_select,
            recordType: fieldDef.meta?.record_type,
            options: fieldDef.meta?.options,
          },
        };
      }
      default:
        return {
          component: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 1024,
          },
        };
    }
  }

  setValueFormatterSelector(fieldDef, relatedToRedirectConfig?: any, data?: any): (params: any) => string {
    if (fieldDef?.meta?.value_hidden) {
      return (params) => {
        return 'REDACTED';
      };
    }
    return (params) => {
      const value = params.value;
      if (value === undefined || value === null) {
        return '';
      }

      switch (fieldDef?.type) {
        case 'date':
        case 'datetime':
          return moment(value).format('MM/DD/YYYY');

        case 'select':
          let options = fieldDef.meta?.options;
          if (options?.length > 0) {
            options = fieldDef.meta.options.map((value) => ({ id: value, name: value }));
          } else {
            options = data?.options[fieldDef.meta.option_name]
              ?.filter((option) => option && !option?.hidden)
              .map((option) => ({ id: option.id, name: option.name }));
          }
          const selectedOption = options?.find((option) => option.id == value);
          return selectedOption ? selectedOption.name : value;
        case 'number':
          if (!fieldDef.meta?.scale) return formatNumber(value, { precision: 0 });
          const parsedValue = parseFloat(value);
          return !isNaN(parsedValue) ? formatNumber(parsedValue, { precision: fieldDef.meta?.scale || 0 }) : '';

        case 'money':
          const moneyValue = parseFloat(value);
          return !isNaN(moneyValue) ? formatMoney(moneyValue, { precision: fieldDef.meta?.scale || 0 }) : '';

        case 'percent':
          const percentValue = parseFloat(value);
          return !isNaN(percentValue) ? `${formatNumber(percentValue, { precision: fieldDef.meta?.scale || 0 })}%` : '';

        default:
          return value;
      }
    };
  }

  // Extension hooks for custom record types. Subclasses may override.
  protected getRecordTypeCellEditor(
    fieldDef: ColumnDef,
    relatedToRedirectConfig: any,
    data?: any,
    optionsFilterFn: OptionsFilterFn = null,
  ): Partial<ColDef> | null {
    return null;
  }

  protected getRecordTypeCellEditorSelector(fieldDef: ColumnDef, relatedToRedirectConfig: any, data?: any): any | null {
    return null;
  }

  protected getRecordTypeCellRenderer(fieldDef: ColumnDef, relatedToRedirectConfig: any): ColDef | null {
    return null;
  }

  protected getRecordTypeCellRendererSelector(
    fieldDef: ColumnDef,
    relatedToRedirectConfig?: any,
    data?: any,
  ): any | null {
    return null;
  }
}
